import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMAGES, scrollToSection } from "@/lib/scroll";
import { useSettings } from "@/lib/SettingsContext";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, delay, ease: EASE },
});

const Checklist = ({ items, dark }) => (
  <ul className="mt-5 space-y-2">
    {items.map((it) => (
      <li key={it} className={`flex items-start gap-2.5 text-sm leading-relaxed ${dark ? "text-[#FAF8F5]/75" : "text-[#4A5A52]"}`}>
        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#D4AF37]" />
        {it}
      </li>
    ))}
  </ul>
);

export default function Services() {
  const settings = useSettings();
  const price = (id) =>
    settings.services?.find((s) => s.id === id)?.price_label || "";

  return (
    <section id="services" className="py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...reveal()} className="mb-16 max-w-2xl">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-[#1B523F]">
            Services & Rates
          </p>
          <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#121A16] sm:text-5xl lg:text-6xl">
            Every visit,
            <br />
            <em className="italic text-[#0F382A]">a signature finish.</em>
          </h2>
        </motion.div>

        <div data-testid="services-grid" className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {/* End of Tenancy — hero card */}
          <motion.article
            {...reveal(0.05)}
            data-testid="service-card-tenancy"
            className="group relative overflow-hidden rounded-[1.75rem] md:col-span-7 md:row-span-2"
          >
            <img
              src={IMAGES.livingRoom}
              alt="End of tenancy clean interior"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-[#0B1D16]/55 transition-colors duration-500 group-hover:bg-[#0B1D16]/45" />
            <div className="relative flex h-full min-h-[26rem] flex-col justify-end p-8 lg:p-12">
              <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">
                Signature Service
              </p>
              <h3 className="font-serif text-3xl font-light text-[#FAF8F5] lg:text-5xl">
                End of Tenancy
              </h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-[#FAF8F5]/80">
                The deposit-back clean. Agency-approved checklist, inside
                appliances, skirting to ceiling — guaranteed to pass inspection.
              </p>
              <p className="mt-6 font-serif text-2xl italic text-[#D4AF37]">{price("tenancy")}</p>
            </div>
          </motion.article>

          {/* Regular Domestic */}
          <motion.article
            {...reveal(0.12)}
            data-testid="service-card-domestic"
            className="group rounded-[1.75rem] border border-[#E5DEC3] bg-white p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(11,29,22,0.25)] md:col-span-5 lg:p-10"
          >
            <div className="flex items-start justify-between">
              <h3 className="font-serif text-3xl font-light text-[#121A16]">Regular Domestic</h3>
              <ArrowUpRight className="h-5 w-5 text-[#D4AF37] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            <p className="mt-2 font-serif text-xl italic text-[#1B523F]">{price("domestic")}</p>
            <Checklist
              items={[
                "Weekly or fortnightly upkeep with the same vetted cleaner",
                "Kitchens, bathrooms, dusting, vacuuming and mopping",
                "Linen changes and ironing on request",
              ]}
            />
          </motion.article>

          {/* Deep Clean */}
          <motion.article
            {...reveal(0.18)}
            data-testid="service-card-deep"
            className="group rounded-[1.75rem] bg-[#0F382A] p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(11,29,22,0.5)] md:col-span-5 lg:p-10"
          >
            <div className="flex items-start justify-between">
              <h3 className="font-serif text-3xl font-light text-[#FAF8F5]">Deep Clean</h3>
              <ArrowUpRight className="h-5 w-5 text-[#D4AF37] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            <p className="mt-2 font-serif text-xl italic text-[#D4AF37]">{price("deep")}</p>
            <Checklist
              dark
              items={[
                "Top-to-bottom reset for spring, post-builders or pre-sale",
                "Limescale removal, degreasing, skirtings and frames",
                "Eco-conscious products, safe for children and pets",
              ]}
            />
          </motion.article>

          {/* Oven */}
          <motion.article
            {...reveal(0.1)}
            data-testid="service-card-oven"
            className="group rounded-[1.75rem] border border-[#E5DEC3] bg-[#F4ECE1] p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(11,29,22,0.2)] md:col-span-6 lg:p-10"
          >
            <h3 className="font-serif text-3xl font-light text-[#121A16]">Oven Detailing</h3>
            <p className="mt-2 font-serif text-xl italic text-[#1B523F]">{price("oven")}</p>
            <p className="mt-4 text-sm leading-relaxed text-[#4A5A52]">
              Racks, glass, hob and extractor restored to showroom condition
              with fume-free, non-caustic solutions.
            </p>
          </motion.article>

          {/* Commercial */}
          <motion.article
            {...reveal(0.16)}
            data-testid="service-card-commercial"
            className="group rounded-[1.75rem] border border-[#1E3B30] bg-[#0B1D16] p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(11,29,22,0.55)] md:col-span-6 lg:p-10"
          >
            <h3 className="font-serif text-3xl font-light text-[#FAF8F5]">Commercial & Office</h3>
            <p className="mt-2 font-serif text-xl italic text-[#D4AF37]">{price("commercial")}</p>
            <p className="mt-4 text-sm leading-relaxed text-[#FAF8F5]/70">
              Daily or weekly contracts for offices, studios and retail across
              Essex. Out-of-hours teams, fully insured.
            </p>
          </motion.article>
        </div>

        <motion.div {...reveal(0.1)} className="mt-12">
          <button
            data-testid="services-cta-button"
            onClick={() => scrollToSection("#booking")}
            className="rounded-full border border-[#0F382A] px-8 py-3.5 text-sm font-semibold text-[#0F382A] transition-[background-color,color] duration-300 hover:bg-[#0F382A] hover:text-[#FAF8F5]"
          >
            Discuss your space
          </button>
        </motion.div>
      </div>
    </section>
  );
}
